/* eslint-disable node/prefer-global/process */
import { auctionCarsQueryKey } from '@autobid/ui/composables/car/data/useAuctionCars'
import { getBarometerQueryKey } from '@autobid/ui/composables/car/data/useBarometerCar'
import { announcementCarQueryKey } from '@autobid/ui/composables/car/data/useCurrentCarAnnouncement'
import { carDetailsQueryKey } from '@autobid/ui/composables/car/data/useDetailsCar'
import { noFilterCountersQueryKey } from '@autobid/ui/composables/car/data/useNoFilterCounters'
import { searchCarsQueryKey } from '@autobid/ui/composables/car/data/useSearchCars'
import { allBidsQueryKey } from '@autobid/ui/composables/car/useAllBids'
import { carTimersQueryKey } from '@autobid/ui/composables/car/useCarTimers'
import { singleAuctionQueryKey } from '@autobid/ui/composables/useAuction'
import { auctionListQueryKey } from '@autobid/ui/utils/auctions/auctionListQueryKey'
import { useQueryClient } from '@tanstack/vue-query'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

export function useAutomaticallyRefreshAuction() {
  const { $socketConnectionId } = useNuxtApp()
  const queryClient = useQueryClient()
  const refreshDebounce = ref<NodeJS.Timeout>()
  const DEBOUNCE_TIME = 300
  const refreshing = ref(false)
  const lastVisibleAt = ref()

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      lastVisibleAt.value = new Date().getTime()
      return
    }

    const isInStandaloneMode
      = window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator && 'standalone' in window.navigator && window.navigator?.standalone)
        || document.referrer.includes('android-app://')

    const now = new Date().getTime()
    if (lastVisibleAt.value + 30_000 < now || isInStandaloneMode) {
      // browser tab refocused and data may be stale already
      $socketConnectionId.value++
    }
  }

  const handleWsReconnect = () => {
    clearTimeout(refreshDebounce.value)

    refreshDebounce.value = setTimeout(async () => {
      queryClient.invalidateQueries(getBarometerQueryKey({ type: 'item' }))
      queryClient.invalidateQueries(getBarometerQueryKey({ type: 'current' }))
      queryClient.invalidateQueries(auctionCarsQueryKey.all())
      queryClient.invalidateQueries(announcementCarQueryKey.all())
      queryClient.invalidateQueries(searchCarsQueryKey.all())
      queryClient.invalidateQueries(singleAuctionQueryKey.all())
      queryClient.invalidateQueries(carDetailsQueryKey.all())
      queryClient.invalidateQueries(allBidsQueryKey.all())
      queryClient.invalidateQueries(noFilterCountersQueryKey.all())
      queryClient.invalidateQueries(carTimersQueryKey.all())
      queryClient.invalidateQueries(auctionListQueryKey.all())
    }, DEBOUNCE_TIME)
  }

  if (process.client) {
    watch($socketConnectionId, handleWsReconnect)

    onMounted(() => {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })

    onBeforeUnmount(() => {
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange,
      )
      clearTimeout(refreshDebounce.value)
    })
  }

  return { refreshing }
}
